import { useEffect, useRef, useState } from 'react';

type SheetDragOptions = {
  /** The drag was let go past `threshold`: play the close animation, then close. */
  onClose: () => void;
  /** False keeps the sheet following the finger but always snaps it back. */
  closable?: boolean;
  threshold?: number;
};

/**
 * Drag a bottom sheet down by its top area to close it. Put `onDragStart` on that area
 * (with `touch-action: none`, or the browser takes the drag as a scroll), and while
 * `isDragging` set `translateY(dragY)` on the sheet with its transition off. A press on
 * a button inside the area stays a tap. Only downward movement counts.
 */
export const useSheetDragToClose = ({
  onClose,
  closable = true,
  threshold = 100,
}: SheetDragOptions) => {
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const lastDragY = useRef(0);
  const cleanupDragRef = useRef<(() => void) | null>(null);

  // Unmounted mid-drag: drop the document listeners.
  useEffect(() => {
    return () => cleanupDragRef.current?.();
  }, []);

  const onDragStart = (e: React.PointerEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).closest('button')) return;

    setIsDragging(true);
    dragStartY.current = e.clientY;
    lastDragY.current = 0;
    setDragY(0);

    const handleMove = (ev: PointerEvent) => {
      const delta = Math.max(0, ev.clientY - dragStartY.current);
      lastDragY.current = delta;
      setDragY(delta);
    };

    const cleanup = () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleEnd);
      document.removeEventListener('pointercancel', handleEnd);
      cleanupDragRef.current = null;
    };

    const handleEnd = () => {
      setIsDragging(false);
      cleanup();
      if (closable && lastDragY.current > threshold) {
        setDragY(0);
        onClose();
      } else {
        requestAnimationFrame(() => setDragY(0));
      }
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleEnd);
    document.addEventListener('pointercancel', handleEnd);

    cleanupDragRef.current = cleanup;
  };

  return { dragY, isDragging, onDragStart };
};
